import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { loadStripe, type Stripe, type StripeCardElement } from '@stripe/stripe-js'
import type { BookingForm, BookingPayment as BookingPaymentDetails, BookingSelection, BookingService } from '../types'
import { calculateDepositAmount, formatCurrency, formatDateLong } from './helpers'

const publishableKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY

if (!publishableKey) {
  throw new Error('NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is not set')
}

const stripePromise = loadStripe(publishableKey)

type BookingPaymentProps = {
  service: BookingService
  selection: BookingSelection
  form: BookingForm
  onBack: () => void
  onClose: () => void
  onConfirmPayment: (payment: BookingPaymentDetails) => void
}

export const BookingPayment = ({
  service,
  selection,
  form,
  onBack,
  onClose,
  onConfirmPayment,
}: BookingPaymentProps) => {
  const [cardholder, setCardholder] = useState(form.fullName)
  const [cardComplete, setCardComplete] = useState(false)
  const [cardError, setCardError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null)
  const cardElementRef = useRef<StripeCardElement | null>(null)
  const cardContainerRef = useRef<HTMLDivElement | null>(null)

  const formattedDate = useMemo(() => formatDateLong(selection.date), [selection.date])
  const depositAmountCents = useMemo(
    () => calculateDepositAmount(service.priceCents, service.depositPercentage),
    [service.priceCents, service.depositPercentage],
  )
  const priceLabel = useMemo(() => formatCurrency(depositAmountCents), [depositAmountCents])
  const depositLabel = useMemo(() => {
    const pct = service.depositPercentage ?? 100
    const base = pct === 100 ? 'Amount due now' : `${pct}% deposit of ${formatCurrency(service.priceCents)}`
    return `${base} — charging ${formatCurrency(depositAmountCents)}`
  }, [service.depositPercentage, service.priceCents, depositAmountCents])

  useEffect(() => {
    let mounted = true
    let card: StripeCardElement | null = null

    stripePromise.then((stripe) => {
      if (!stripe || !mounted) return
      setStripeInstance(stripe)
      const elements = stripe.elements({ appearance: { theme: 'night' } })
      if (!cardContainerRef.current) return
      card = elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            color: '#ffffff',
            fontFamily: "'Inter', 'Lato', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            fontSize: '16px',
            '::placeholder': { color: 'rgba(255,255,255,0.6)' },
          },
          invalid: {
            color: '#fca5a5',
            iconColor: '#fca5a5',
          },
        },
      })
      card.mount(cardContainerRef.current)
      card.on('change', (event) => {
        if (!mounted) return
        setCardComplete(event.complete)
        setCardError(event.error?.message || null)
      })
      cardElementRef.current = card
    })

    return () => {
      mounted = false
      card?.destroy()
      cardElementRef.current = null
      setCardComplete(false)
    }
  }, [])

  const canSubmit = cardholder.trim().length > 1 && cardComplete && !!stripeInstance

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit || processing) return

    setProcessing(true)
    setError(null)

    try {
      const stripe = stripeInstance
      const cardElement = cardElementRef.current
      if (!stripe || !cardElement) {
        throw new Error('Card form is not ready yet. Please try again.')
      }

      const apiUrl = typeof window !== 'undefined' ? window.location.origin : ''
      const intentResponse = await fetch(`${apiUrl}/api/stripe/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amountCents: depositAmountCents,
          currency: 'usd',
          metadata: {
            serviceId: service.id,
            serviceName: service.name,
            depositPercentage: service.depositPercentage ?? 100,
            bookingDate: selection.date,
            bookingSlot: selection.slot,
            customerEmail: form.email,
          },
        }),
      })

      if (!intentResponse.ok) {
        const message = await intentResponse.text()
        throw new Error(message || 'Failed to start payment')
      }

      const { clientSecret } = await intentResponse.json()
      if (!clientSecret) {
        throw new Error('Missing payment intent client secret')
      }

      const confirmation = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: cardholder,
            email: form.email,
          },
        },
        receipt_email: form.email,
      })

      if (confirmation.error || !confirmation.paymentIntent) {
        throw new Error(confirmation.error?.message || 'Payment failed')
      }

      const paymentIntent = confirmation.paymentIntent
      const cardDetails = (paymentIntent as any).charges?.data?.[0]?.payment_method_details?.card

      onConfirmPayment({
        method: 'stripe',
        brand: cardDetails?.brand || 'Card',
        last4: cardDetails?.last4 || '0000',
        amountCents: depositAmountCents,
        status: confirmation.paymentIntent.status === 'succeeded' ? 'confirmed' : 'pending',
        stripePaymentIntentId: paymentIntent.id,
      })
    } catch (err) {
      console.error('[BookingPayment] Stripe error', err)
      setError(err instanceof Error ? err.message : 'Payment failed, please try again')
    } finally {
      setProcessing(false)
    }
  }

  return (
    <section className="flex max-h-full flex-col gap-4 overflow-y-auto rounded-lg border border-slate-200/40 bg-white p-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-slate-900">Pre-payment required</p>
          <p className="mt-1 text-xs text-slate-500">
            The service provider has enabled a pre-payment system. Please finish payment to successfully book your
            appointment.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close booking flow"
          className="rounded-lg border border-transparent px-2 py-1 text-xl leading-none text-slate-500 transition hover:bg-slate-200/70"
        >
          ×
        </button>
      </header>

      <div className="rounded-lg border border-slate-200/40 bg-slate-50 p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Appointment</p>
        <p className="mt-1 text-sm font-semibold text-slate-900">
          {service.name} · {service.durationMinutes} min
        </p>
        <p className="mt-1 text-sm text-slate-600">
          {formattedDate} · {selection.slot}
        </p>
        <p className="mt-3 text-sm text-slate-500">Confirmation sent to {form.email}</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="rounded-xl border border-slate-200/70 bg-slate-900 text-white shadow-sm">
          <div className="border-b border-white/10 px-4 py-3">
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Secure card payment</p>
            <p className="text-lg font-semibold">{priceLabel}</p>
            {depositLabel && (
              <p className="text-[11px] text-white/70">{depositLabel}</p>
            )}
          </div>
          <div className="flex flex-col gap-3 px-4 py-4">
            <label className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
              Cardholder name
              <input
                value={cardholder}
                onChange={(event) => setCardholder(event.target.value)}
                className="mt-1 w-full rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-base text-white placeholder:text-white/60 outline-none focus:border-white"
                placeholder="Jane Doe"
                style={{ fontSize: '16px' }}
              />
            </label>
            <label className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
              Card details
              <div
                ref={cardContainerRef}
                className="mt-2 rounded-lg border border-white/20 bg-white/5 px-3 py-3 text-base"
                style={{ minHeight: '52px' }}
              />
            </label>
            {cardError && <p className="text-xs text-red-300">{cardError}</p>}
          </div>
          <div className="flex items-center justify-between border-t border-white/10 px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-white/60">
            <span className="font-semibold">Powered by</span>
            <span className="text-base font-semibold tracking-normal">Stripe</span>
          </div>
        </div>

        {error && <p className="text-sm font-semibold text-red-500">{error}</p>}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-slate-200/70 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-white"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={!canSubmit || processing}
            className="rounded-lg bg-accent px-4 py-3 text-sm font-semibold text-white transition disabled:opacity-50"
          >
            {processing ? 'Processing…' : `Pay ${priceLabel}`}
          </button>
        </div>
      </form>
    </section>
  )
}
